import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChromePicker } from 'react-color';
import { Box, IconButton, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { FaPlus, FaTrash } from 'react-icons/fa';
import {useDispatch, useSelector} from "react-redux";
import { fabric } from 'fabric';
import {updateColor} from "../../store/reducers/gradientSlice";
import {setColorType, toggleTriggerPallet} from "../../store/reducers/colorReducer";
import {setPanelKey} from "../../store/reducers/panleKey";
import {setActiveColorSetting} from "../../store/reducers/canvasSlice";


function GradientManager({ isGradient }) {
    const [colors, setColors] = useState(['#000000']);
    const [newColor, setNewColor] = useState('');
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [selectedColorIndex, setSelectedColorIndex] = useState(null);
    const { canvas, activeObject } = useSelector((state) => state.canvas);
    const {activeGradientCol} = useSelector((state) => state.colorSlice);

    const dispatch = useDispatch()

    const colorPickerRef = useRef();

    useEffect(() => {
        if (activeObject) {
            const newColors = activeObject.fill.hasOwnProperty('type')
                ? activeObject.fill.colorStops.map(m => m.color)
                : [activeObject.fill];
            setColors(newColors);
        }
        const handleClickOutside = (event) => {
            if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
                setShowColorPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [activeObject, activeGradientCol]);

    useEffect(() => {
        if (!isGradient && activeObject) {
            dispatch(updateColor({ index: 0, color: '#000000' }));
            setColors(['#000000']);
            applyColor('#000000');
        }
    }, [isGradient]);

    const applyColor = useCallback((color) => {
        if (activeObject) {
            activeObject.set({ fill: color });
            canvas.renderAll();
        }
    }, [activeObject, canvas]);

    const applyGradient = useCallback((newColors) => {
        if (!newColors.length || !activeObject) return;

        if (newColors.length === 1) {
            applyColor(newColors[0]);
        } else {
            const colorStops = newColors.map((color, index) => ({
                offset: index / (newColors.length - 1),
                color: color
            }));
            let gradient = new fabric.Gradient({
                type: "linear",
                coords: {
                    x1: 0,
                    y1: activeObject.height / 2,
                    x2: activeObject.width,
                    y2: activeObject.height / 2
                },
                colorStops: colorStops
            });
            activeObject.set({ fill: gradient, dirty: true });
            canvas.renderAll();
        }
    }, [activeObject, canvas]);

    const handleColorChange = useCallback((color) => {
        setNewColor(color.hex);
        if (selectedColorIndex !== null) {
            const updatedColors = [...colors];
            updatedColors[selectedColorIndex] = color.hex;
            dispatch(updateColor({ index: selectedColorIndex, color: color.hex }));
            setColors(updatedColors);
            applyGradient(updatedColors);
        }
    }, [selectedColorIndex, colors, applyGradient]);

    const handleDeleteColor = useCallback((index) => {
        if (colors.length === 1) return;
        const updatedColors = colors.filter((_, i) => i !== index);
        dispatch(updateColor({ index, color: updatedColors }));
        setColors(updatedColors);
        applyGradient(updatedColors);
    }, [colors, applyGradient]);

    const handleEditColor = (index) => {
        setNewColor(colors[index]);
        setSelectedColorIndex(index);
        dispatch(setColorType('shadow'))
        dispatch(setPanelKey('colorPicker'))
        dispatch(setActiveColorSetting({gradientColor:colors[index],index:index}))
        dispatch(setColorType('gradient'))
        dispatch(toggleTriggerPallet(''))
        // setShowColorPicker(true);
    };

    const handleAddColor = () => {
        const tempArr = [...colors, '#000000'];
        dispatch(updateColor({ index: colors.length, color: '#000000' }));
        setColors(tempArr);
        applyGradient(tempArr)
    };

    return (
        <Box>
            {showColorPicker ? (
                <Box sx={{width:'fit-content'}} ref={colorPickerRef}>
                    <ChromePicker color={newColor} onChange={handleColorChange} />
                </Box>
            ) : (
                <IconButton onClick={handleAddColor}>
                    <FaPlus />
                </IconButton>
            )}
            <Box sx={{m:1}}>Colors</Box>
            <List>
                {colors.map((color, index) => (
                    <ListItem key={index} sx={{ padding: '7px' }}>
                        <ListItemIcon onClick={() => handleEditColor(index)}>
                            <Box className={'color-preview'} sx={{ cursor: "pointer" }} bgcolor={color} />
                        </ListItemIcon>
                        <ListItemText primary={color} />
                        <IconButton onClick={() => handleDeleteColor(index)} sx={{ height: '32px', width: '32px' }}>
                            <FaTrash />
                        </IconButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );
}

export default React.memo(GradientManager);
